// @flow
import type { EventCreateRequest } from "redux/api-types/EventCreate";
import type { EventContent } from "redux/api-types/EventContent";
import type { EventTemplate } from "redux/api-types/EventTemplate";
import type { BusinessLocationRecord } from "redux/modules/createCabelasClass/records/BusinessLocationRecord";
import { parseScheduleEventsForms } from "redux/modules/createCabelasClass/parsers/form";
import { normalizeChapterEventType } from "redux/modules/instructorChapters/constants";
import { isChapterLocation } from "redux/modules/businessEventScheduling/instructorLocations";
import {
  getEventContentFormFields,
  getEventContentFlags,
} from "redux/modules/createCabelasClass/parsers/eventContentForm";
import { getApprovedContentPricing } from "redux/modules/createCabelasClass/parsers/costOptionsForm";
import { getMaxCapacity } from "redux/modules/createCabelasClass/parsers/capacity";
import { getDurationBounds } from "redux/modules/createCabelasClass/parsers/duration";
import { isSupportedEventTimezone } from "redux/modules/createCabelasClass/parsers/timezones";
import { isContentForChapterLocation } from "./contentEligibility";
import { parseTypedAddress } from "./typedAddress";
import type { ChapterEventFormValues } from "./typedAddress";
import validate from "containers/InstructorCabelasClasses/ScheduleClassesDialog/validate";

export const parseChapterEvents = (
  values: ChapterEventFormValues,
  userId: string,
  location: BusinessLocationRecord,
  content: ?EventContent,
  templates: Array<EventTemplate>
): Array<EventCreateRequest> => {
  if (
    !isChapterLocation(location) ||
    values.locationId !== location.locationId
  ) {
    throw new Error("Choose an assigned Chapter venue.");
  }
  const eventType = normalizeChapterEventType(content?.attributes.eventType);
  if (
    !content ||
    !isContentForChapterLocation(content, location) ||
    !eventType
  ) {
    throw new Error("Choose event content for the selected Chapter business.");
  }
  if (!isSupportedEventTimezone(values.timezone)) {
    throw new Error("Choose a supported time zone for the venue.");
  }
  const venue = values.useTypedAddress
    ? parseTypedAddress(values.typedAddress)
    : undefined;
  const pricing = getApprovedContentPricing(
    content.attributes.costOptions,
    templates
  );
  if (pricing.error) {
    throw new Error(pricing.error);
  }
  // Rebuild backend-owned choices from current scoped lookups, not hidden form fields.
  const checkedValues = {
    ...values,
    ...getEventContentFormFields(content),
    ...getEventContentFlags(content),
    ...getDurationBounds(content.attributes),
    ...pricing,
    pricingError: "",
    maxCapacity: getMaxCapacity(content.attributes.capacity),
    hostIds: location.hostIds,
    locationId: location.locationId,
  };
  const errors = validate(checkedValues);
  if (Object.keys(errors).length) {
    throw new Error(
      "Review the required fields, dates, capacities, and durations before saving."
    );
  }
  return parseScheduleEventsForms(checkedValues, userId, location.businessId, {
    eventType,
    venue,
  });
};
